import { FormattedMessage } from 'react-intl';
import { MoneyNoDecimals } from '../../ui/moneyNoDecimals';
import styles from './FundingRequirementsView.module.css';
import messages from './FundingRequirementsView.messages';
import { Tooltip } from '../../ui/tooltip';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import FundingHeader from '../fundingHeader/FundingHeader';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { getLoanDurationInMonths } from '../../myOffers/MyOffers.helpers';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import FundingSteps from '../fundingSteps/FundingSteps';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { useQuery } from '@tanstack/react-query';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { tracking } from '@iwoca/frontend-tracking-library';
import { useEffect } from 'react';
import { trackPageView } from '../../../tracking/pageView';

const FundingRequirementsView = () => {
    const location = getCurrentLocation();

    let drawdownAmount: string | number | undefined = getQueryParameter(
        location,
        'amount'
    );
    const sweep = getQueryParameter(location, 'sweep');

    const offerId: string | undefined = getQueryParameter(location, 'offerId');

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'getQueryParameter drawdownAmount is undefined',
            {
                tags: {
                    reporter: 'funding_requirements_view'
                }
            }
        );
    }
    if (!offerId) {
        IwDataDogLogger.logError('getQueryParameter offerId is undefined', {
            tags: {
                reporter: 'funding_requirements_view'
            }
        });
    }

    const stateKey = useStateKey();

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    const isInterestOnlyExtension =
        (formalOffer?.interest_only_repayments || 0) > 0 &&
        drawdownAmount === '0';

    useEffect(() => {
        if (stateKey) {
            trackPageView(stateKey, 'Funding Requirements');
        }
    }, [stateKey]);

    const { isLoading: loadingPostOfferConfiguration } = useQuery({
        // eslint-disable-next-line @tanstack/query/exhaustive-deps
        queryKey: ['offer_configuration', stateKey, offerId],
        queryFn: async () => {
            // eslint-disable-next-line pandora/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/formal_offers/${stateKey}/${
                    formalOffer!.product_type
                }/${formalOffer!.offer_id}/configuration/`,
                {
                    method: 'GET'
                }
            );
            return response.json();
        },
        enabled: Boolean(formalOffer)
    });

    if (loadingFormalOffer || loadingPostOfferConfiguration) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}></div>
            </MainSectionWrapper>
        );
    }

    if (formalOffer && drawdownAmount) {
        drawdownAmount = parseInt(drawdownAmount);

        tracking.ampli.drawdownStarted({
            amount: drawdownAmount,
            formalOfferId: offerId!
        });

        const loanDuration: number = getLoanDurationInMonths(
            formalOffer.duration!
        );

        return (
            <>
                <FundingHeader
                    header={
                        <FormattedMessage
                            {...(isInterestOnlyExtension
                                ? messages.interestOnlyHeader
                                : messages.header)}
                            values={{
                                drawdownAmount: (
                                    <span className={styles.headerBlue}>
                                        <MoneyNoDecimals
                                            value={drawdownAmount}
                                        />
                                    </span>
                                ),
                                loanDuration: (
                                    <span className={styles.headerBlue}>
                                        <FormattedMessage
                                            {...messages.duration}
                                            values={{
                                                loanDuration: loanDuration
                                            }}
                                        />
                                    </span>
                                ),
                                tooltip: (
                                    <span>
                                        <Tooltip
                                            className={styles.tooltip}
                                            message={
                                                <FormattedMessage
                                                    {...messages.tooltip}
                                                />
                                            }
                                            placement="auto"
                                        />
                                    </span>
                                )
                            }}
                        />
                    }
                    subHeader={
                        <FormattedMessage
                            {...(isInterestOnlyExtension
                                ? messages.interestOnlySubHeader
                                : messages.subHeader)}
                        />
                    }
                />

                <MainSectionWrapper>
                    <div className={styles.container}>
                        <FundingSteps
                            formalOffer={formalOffer}
                            drawdownAmount={drawdownAmount}
                            sweep={sweep}
                            isInterestOnlyExtension={isInterestOnlyExtension}
                        />
                    </div>
                </MainSectionWrapper>
            </>
        );
    }
    return <FallbackErrorModal />;
};

// eslint-disable-next-line import/no-default-export
export default FundingRequirementsView;
